import React, { useMemo, useState, useEffect, useCallback } from "react";
import moment from "moment";

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer1";

const DatatableTables = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [search, setSearch] = useState("");
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchData = useCallback(async () => {
    if (!user || !token) return;

    try {
      setLoading(true);
      const params = {
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      };

      let apiUrl = "";

      if (user?.Role === "Student") {
        apiUrl = "https://api.kunalsdrumclass.com/api/GetStudentNotifications";
        params.studentId = user?.Id;
      } else {
        apiUrl = "https://api.kunalsdrumclass.com/api/GetAllNotifications";
      }

      const query = new URLSearchParams(params).toString();
      const res = await fetch(`${apiUrl}?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, search]);

  const updateCount = useCallback(async () => {
    if (!user || !token) return;

    let isAdmin;
    let userId;

    if (user?.Role === "Student") {
      isAdmin = 0;
      userId = user?.LoginId;
    } else {
      isAdmin = 1;
      userId = user?.Id;
    }

    const payload = { userId, isAdmin };

    try {
      await fetch(
        "https://api.kunalsdrumclass.com/api/UpdateNotificationStatus",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
    } catch (err) {
      console.error("Update count error:", err);
    }
  }, [user, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    updateCount();
  }, [updateCount]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const columns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Name",
        meta: {
          sticky: true,
        },
        accessorKey: "UserName",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Type",
        accessorKey: "Type",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Title",
        accessorKey: "Title",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Message",
        accessorKey: "Message",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Read",
        accessorKey: "IsRead",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const isActive = getValue() === 1;

          return (
            <span
              className={`badge ${isActive ? "bg-success" : "bg-danger"
                }`}
            >
              {isActive ? "Read" : "Unread"}
            </span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "Status",
        enableSorting: true,
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const isActive = getValue() === "Sent";

          return (
            <span
              className={`badge ${isActive ? "bg-success" : "bg-danger"
                }`}
            >
              {isActive ? "Sent" : "Failed"}
            </span>
          );
        },
      },
      {
        header: "Sent Time",
        accessorKey: "Sent_At",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info) => moment(info.getValue()).format("DD-MM-YYYY hh:mm A"),
      }
    ],
    []
  );

  //meta title
  document.title = "Notifications | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Notifications"
          breadcrumbItems={[
            { title: "Notifications" }
          ]}
        />

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />
      </div>
    </div>
  );
};

export default DatatableTables;
